import * as angular from 'angular';
import * as _ from 'lodash';
import { ILogbookTableService } from '@services/widgets';

import {
    AggregationMode,
    ComponentDefinition,
    DigitalSetSimpleDictionary,
    GroupDisplayName,
    IValueViewModel,
    LogbookHistorianStorageQueryResult,
    NameBuilderConfiguration,
    TimePeriod
} from '../../../../models/index';
import { getLogbookTableHeadings, getTimeRange, HeadingDefinition, LogbookTableColumnsConfiguration, LogbookTableSeries } from '../../../../widgets/logbookTable';
import { LogbookSeriesValues } from 'widgets/logbookTable/LogbookSeriesValues';

class LogbookStorageResultsController implements angular.IComponentController {
    configuration: LogbookTableColumnsConfiguration & { series?: LogbookTableSeries[]; };
    displayedEntries: any[];
    headings: HeadingDefinition[][];
    nameConfiguration: NameBuilderConfiguration;

    readonly digitalSets: DigitalSetSimpleDictionary;
    readonly isLoading: boolean;
    readonly resultByLogbook: LogbookHistorianStorageQueryResult[];
    readonly viewModels: { [guid: string]: IValueViewModel; };

    static $inject = [
        '$translate',
        'logbookTableService'
    ];
    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _logbookTableService: ILogbookTableService
    ) {
    }

    $onInit() {
        const configuration: LogbookTableColumnsConfiguration & { series?: LogbookTableSeries[] } = {
            groupNameDisplayed: GroupDisplayName.UniqueName,
            hasCategories: true,
            hasDateModified: false,
            hasDescription: true,
            hasDuration: false,
            hasSeverity: true,
            hasUser: false,
            hasIdentifierName: true,
            timeRange: TimePeriod.Both
        };
        this.configuration = configuration;
        this.nameConfiguration = {
            hasGroupName: false,
            hasName: true,
            hasShortComment: false,
            hasUnit: false,
            hasValueType: false,
        };
        const headings = getLogbookTableHeadings(configuration, [], {});
        headings[0].splice(1, 0, { label: 'ID', classed: { 'plus-column': true } });
        this.headings = headings;
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['resultByLogbook']?.currentValue) {
            const seriesNames = _.flatten(this.resultByLogbook.map(d => d.values.filter(d => !!d.series).map(s => s.series.name)));
            const toMap = _.uniq(seriesNames)
                .sort((a, b) => a < b ? -1 : 1);
            const headings = getLogbookTableHeadings(this.configuration, [], {});
            headings[0].splice(1, 0, { label: 'ID', classed: { 'plus-column': true } });
            if (toMap.length) {
                toMap.forEach(s => {
                    headings[0].push({
                        label: s
                    });
                });
            }
            this.headings = headings;
            this.displayedEntries = this.resultByLogbook
                .map(dto => {
                    const { entry, values } = dto;
                    const inProgress = this._$translate.instant('In progress');
                    const range = {
                        startDate: entry.beginDateTime,
                        endDate: entry.endDateTime
                    };
                    const series = toMap.map(name => {
                        const d = _.find(values, v => v?.series?.name === name);
                        if (!!d && d.series && d.value) {
                            return {
                                aggregationMode: d.value.aggregationMode,
                                guid: d.value.guid,
                                isBoundToGroup: false,
                                originalGuid: d.value.guid,
                                value: d.value.valueType,
                                decimals: 2
                            };
                        } else {
                            return {
                                aggregationMode: AggregationMode.Average,
                                guid: '',
                                isBoundToGroup: false,
                                originalGuid: '',
                                value: '',
                                decimals: 2
                            };
                        }
                    });
                    const seriesValues = values.map(d => d.value);
                    const allValues: LogbookSeriesValues[] = [{ logbookEntryId: entry.logbookEntryId, values: seriesValues }];
                    const mappedValues = this._logbookTableService.mapEntryValues(entry.logbookEntryId, series, allValues, this.viewModels, {}, this.digitalSets);
                    const result = Object.assign({}, entry, { displayRange: getTimeRange(range, this.configuration.timeRange, null, inProgress), values: mappedValues });
                    return result;
                });
        }
    }
}

export const wtLogbookStorageResults: ComponentDefinition = {
    componentId: 'wtLogbookStorageResults',
    controller: LogbookStorageResultsController,
    template: require('./logbookStorageResults.component.html'),
    controllerAs: 'vm',
    bindings: {
        digitalSets: '<',
        isLoading: '<',
        resultByLogbook: '<',
        viewModels: '<'
    }
};
